// ADR 1.10.26n §2 — the route table's two helpers, moved verbatim out of
// `router.tsx` so `accountRoutes.tsx` can share them. Exporting them from
// `router.tsx` instead would make the two files import each other, and `page`
// (a `const`) would be read before it is initialised.

import type { ComponentType } from 'react'

import { CONSOLE_NAMESPACE, ensureNamespace, i18next } from '../i18n/index'
import type { RouteAppsHandle, RouteTitleHandle } from './RouteAnnouncer'
import { gated } from './wasmGate'

/**
 * ADR 24.9.26l §3 — each leaf route names its page title. `RouteAnnouncer`
 * reads the deepest match's; 24.9.26l AC6 fails a leaf without one.
 * ADR 28.9.26e §7 — `backTo` gives the header a back arrow (Android's
 * `TopAppBar` navigation icon), so the route table, not the header, owns it.
 */
export function titled(titleKey: string, backTo?: string): RouteTitleHandle {
  return backTo === undefined ? { titleKey } : { titleKey, backTo }
}

/**
 * ADR 6.10.26l D5 — the header's Apps icon on this route, spread beside
 * `titled(…)`. A route without it draws no icon, so the console, account, auth
 * and shared-plan pages are unchanged by construction.
 */
export function withApps(apps: RouteAppsHandle): { apps: RouteAppsHandle } {
  return { apps }
}

/**
 * ADR 24.9.26ag §2 — a lazily-loaded leaf. `handle` stays static on the route
 * object (24.9.26l AC6): react-router prefers a static property over one `lazy`
 * returns, and the title walk reads the static array. No leaf has an
 * `errorElement` of its own, so a failed chunk load reaches the root's
 * `RouteFailure` (8.9.26p §3). ADR 25.9.26f §4 — through the wasm gate, which
 * fetches the facade beside the page and renders nothing past a failed init.
 */
export const page =
  <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  async () => ({ Component: await gated(load, name) })

/**
 * ADR 8.10.26u §3 — a console leaf: `page`, plus the `console` namespace for the
 * current language and `en`, awaited together inside the `lazy`. The data router
 * commits only after `lazy` resolves, and that commit is what re-renders the
 * header and the announcer, so a cold deep link shows its title translated. A
 * failed `console` chunk rejects the `lazy` and reaches `RouteFailure`, like a
 * failed page chunk.
 */
export const consolePage =
  <K extends string>(load: () => Promise<Record<K, ComponentType>>, name: K) =>
  async () => {
    const [Component] = await Promise.all([
      gated(load, name),
      ensureNamespace(i18next, CONSOLE_NAMESPACE),
    ])
    return { Component }
  }

/**
 * Only a same-site path may be redirected to.
 *
 * `?next=` is attacker-controllable — it is in a link anyone can send — and a
 * value like `//evil.example` is a protocol-relative URL that `<Navigate>` would
 * happily follow off-site, straight after a successful sign-in.
 *
 * ADR 24.9.26h W7-1: the prefix check alone let `/\evil.example` (and a tab or
 * newline after the first slash) through — the URL parser reads a backslash as
 * a slash and strips tabs, so the browser sees `//evil.example`, and React
 * Router's `pushState` falls back to `location.assign` on the cross-origin
 * throw (GHSA-wrjc-x8rr-h8h6). So the value is also resolved the way the
 * browser will resolve it, and must stay on the base's host.
 *
 * Moved here from `router.tsx` (Backend ADR 25.9.26n §5), which re-exports it.
 */
const NEXT_BASE = 'https://next.invalid'

export function safeNextPath(next: string | null): string {
  if (next === null || !next.startsWith('/') || next.startsWith('//')) return '/'
  try {
    if (new URL(next, NEXT_BASE).origin !== NEXT_BASE) return '/'
  } catch {
    return '/'
  }
  return next
}

/**
 * Backend ADR 25.9.26n §5 — a sign-in ↔ register cross-link that keeps the
 * visitor's `?next=`, made safe first, so an app's first-time user who
 * registers comes back to the consent screen rather than to `/`. A missing or
 * unsafe `next` is dropped rather than carried as `/`.
 */
export function withNext(path: string, next: string | null): string {
  const safe = safeNextPath(next)
  return safe === '/' ? path : `${path}?next=${encodeURIComponent(safe)}`
}
